import { getOrCreateMainLogger } from "../logger/main";
import { BusEventPayload, MainProcessBusEvent, WebContentsId } from "../types/types";
import { ipcMain, IpcMainEvent, WebContents } from "electron";

const logger = getOrCreateMainLogger();

/**
 * The MainBus is for internal communications between renderer processes and the main process.
 *
 * (See IAB for communications between Finsemble windows)
 */
class MainBus {
	// Local code in the main process can register for events from renderers
	localHandlers: {
		[eventName: string]: ((event: MainProcessBusEvent) => void)[];
	} = {};

	// Remote code in renderer processes can register for events from the main process
	rendererHandlers: {
		[eventName: string]: WebContents[];
	} = {};

	constructor() {
		this.setupIPCListeners = this.setupIPCListeners.bind(this);
		this.setupIPCListeners();
	}

	/**
	 * Removes all subscriptions for a window. This happens on window close.
	 */
	unsubscribeWebContentsId(closingWebContentsId: WebContentsId) {
		Object.keys(this.rendererHandlers).forEach((eventName) => {
			this.removeRendererListener(closingWebContentsId, eventName);
		});
	}

	/**
	 * Returns an unsubscriber function which can be used to remove the handler
	 */
	addHandler(eventName: string, handler: (event: MainProcessBusEvent) => void) {
		const handlerWrapper = async (event: MainProcessBusEvent) => {
			try {
				// Handler functions may or may not be async. We want to catch and log any exception
				// as well as any Promise rejection, so we need to handle both possibilities here.
				const isAsync = handler.constructor.name === "AsyncFunction";
				isAsync ? await handler(event) : handler(event);
			} catch (e) {
				logger.error(`Unhandled exception in mainBus handler for ${eventName}`, e);
			}
		};
		this.localHandlers[eventName] = (this.localHandlers[eventName] ?? []).concat([handlerWrapper]);
		return () => {
			if (this.localHandlers[eventName]) {
				this.localHandlers[eventName] = this.localHandlers[eventName].filter(
					(existingHandler) => existingHandler !== handlerWrapper
				);
				if (this.localHandlers[eventName].length === 0) delete this.localHandlers[eventName];
			}
		};
	}

	/**
	 * Handles an inbound IPC event (from renderer processes), which is sent to any local handlers
	 */
	private handleRendererRequest(event: IpcMainEvent, finsembleEvent: BusEventPayload, responseUUID: string) {
		if (!finsembleEvent.topic) {
			event.sender.send("handleResponse", {
				responseUUID,
				err: "No topic provided",
			});
			return;
		}

		const busEvent = this.buildRequestObject(event, finsembleEvent, responseUUID);

		(this.localHandlers[finsembleEvent.topic] ?? []).forEach(async (handler) => {
			try {
				// Handler functions may or may not be async. We want to catch and log any exception
				// as well as any Promise rejection, so we need to handle both possibilities here.
				const isAsync = handler.constructor.name === "AsyncFunction";
				isAsync ? await handler(busEvent) : handler(busEvent);
			} catch (e: any) {
				logger.error(`MainBus, error processing inbound event ${finsembleEvent.topic}`, e);
			}
		});
	}

	/**
	 * Called locally from the main process to send an event to any renderer handlers
	 */
	sendEvent(eventTopic: string, data: any = {}) {
		(this.rendererHandlers[eventTopic] ?? []).forEach((webContents) => {
			try {
				webContents.send("systemResponse", {
					topic: eventTopic,
					data: JSON.stringify(data),
				});
			} catch (err: any) {
				// prevents stack overflow exception when transport is unavailable but we keep trying to send the error to Central Logger
				logger.setupLoggingToCentralLogger(false);
				logger.error(`Unable to send event to ${webContents.id} ${err.message}`);
			}
		});
	}

	/**
	 * add a system listener
	 */
	private addRendererListener(event: IpcMainEvent, eventTopic: string): void {
		this.rendererHandlers[eventTopic] = (this.rendererHandlers[eventTopic] ?? []).concat([event.sender]);
		logger.verbose(`Added system listener ${event.sender.id} to ${eventTopic}`);
	}

	/**
	 * remove a system listener
	 */
	private removeRendererListener(webContentsId: WebContentsId, eventTopic: string): void {
		if (this.rendererHandlers[eventTopic]) {
			this.rendererHandlers[eventTopic] = this.rendererHandlers[eventTopic].filter(
				(existingWebContents) => existingWebContents.id !== webContentsId
			);
			if (this.rendererHandlers[eventTopic].length === 0) delete this.rendererHandlers[eventTopic];
		}

		// This can log for every window depending on the timing for shutdown. Making verbose because it's usually irrelevant
		logger.verbose(`Removing system listener listening to ${eventTopic} from ${webContentsId}`);
	}

	/**
	 * sets up three channels 'e2o.mainRequest', 'system.addListener', and 'system.removeListener'
	 * all api communications occur on 'e2o.mainRequest' from the renderer processes.
	 * MainBus emits the sub topic System, Application, window, etc.
	 */
	private setupIPCListeners() {
		ipcMain.on("e2o.mainRequest", this.handleRendererRequest.bind(this));
		ipcMain.on("system.addListener", this.addRendererListener.bind(this));
		ipcMain.on("system.removeListener", (event: IpcMainEvent, eventTopic: string) => {
			this.removeRendererListener(event.sender.id, eventTopic);
		});
		// See Handshake comment in renderer/RequestHelper.js
		ipcMain.on("ping", (event) => {
			event.sender.send("pong");
		});
	}

	/**
	 * Build a MainProcessBusEvent object from an inbound IPC event.
	 * @param {BusEventPayload} args - Information passed from  the renderer request
	 * @param {UUID} responseUUID - The unique channel to respond to the renderer
	 */
	buildRequestObject(event: IpcMainEvent, args: BusEventPayload, responseUUID: string): MainProcessBusEvent {
		return {
			sender: event.sender,
			rawEvent: event,
			rawArgs: args,
			topic: args.topic,
			data: args.data,
			respondSync: (response: any) => {
				event.returnValue = response;
			},
			respond: (response: any) => {
				if (!event.sender) return;
				let cleanResponse;
				let err;
				try {
					cleanResponse = response ? JSON.parse(JSON.stringify(response)) : response;
				} catch (ex) {
					err = `buildRequestObject: respond: Failed to stringify response for ${args.topic}`;
					logger.error(err);
					logger.error(ex);
				}
				try {
					event.sender.send("handleResponse", {
						responseUUID,
						response: cleanResponse,
						err: err,
					});
				} catch (ex) {
					logger.error(`Window is gone, unable to send ${args.topic}`);
				}
			},
		};
	}

	/**
	 * Helper function to generate a topic for a given window.
	 */
	generateTopic(windowName: string, topic: string) {
		return windowName + topic;
	}
}

export const mainBus = new MainBus();
